import { Player as BitmovinPlayer, PlayerEvent, PlayerEventBase } from "bitmovin-player";
import configs from "../configs";
import theme from "../theme";
import { VideoConfig, CueEvent, SourceConfig } from "./interface";

let bitmovinPlayer: InstanceType<typeof BitmovinPlayer> | null = null;

const determineBitmovinSourceType = (url: string, drm?: string, laUrl?: string): SourceConfig => {
    let source;
    if (url.includes(".m3u")) {
        source = { hls: url };
    } else if (url.includes(".mpd")) {
        source = { dash: url };
    } else {
        source = { progressive: url };
    }

    if (drm && laUrl) {
        // @ts-ignore
        source[drm] = {
            LA_URL: laUrl
        };
    }

    //   dash: 'https://cdn.bitmovin.com/content/assets/art-of-motion_drm/mpds/11331.mpd',
    //   hls: 'https://cdn.bitmovin.com/content/assets/art-of-motion_drm/m3u8s/11331.m3u8',
    //   smooth: 'https://test.playready.microsoft.com/smoothstreaming/SSWSS720H264/SuperSpeedway_720.ism/manifest',
    //   drm: {
    //     widevine: {
    //       LA_URL: 'https://cwip-shaka-proxy.appspot.com/no_auth'
    //     },
    //     playready: {
    //       LA_URL: 'https://test.playready.microsoft.com/service/rightsmanager.asmx?PlayRight=1&ContentKey=EAtsIJQPd5pFiRUrV9Layw=='
    //     }
    //   }
    return source;
};

const initializeBitmovinPlayer = (videoEl: HTMLVideoElement, src: string): InstanceType<typeof BitmovinPlayer> => {
    let wrapper = document.getElementById("bitmovin-wrapper");

    if (wrapper) {
        if (bitmovinPlayer) {
            return bitmovinPlayer;
        }
    } else {
        wrapper = document.createElement("div");
        wrapper.id = "bitmovin-wrapper";

        const videoRect = videoEl.getBoundingClientRect();
        const wrapperW = videoRect.width || videoEl.offsetWidth || parseInt(videoEl.getAttribute("width") || "1920");
        const wrapperH = videoRect.height || videoEl.offsetHeight || parseInt(videoEl.getAttribute("height") || "1080");

        Object.assign(wrapper.style, {
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: `${wrapperW}px`,
            height: `${wrapperH}px`,
            zIndex: "1"
        });

        const parent = videoEl.parentElement || document.body;
        parent.insertBefore(wrapper, videoEl.parentElement ? videoEl : null);
    }

    const match = src.match(/\/([^\/]+)\/index\.m3u8/);
    const title = match ? match[1] : undefined;

    const player = new BitmovinPlayer(wrapper, {
        ui: false,
        playback: {
            autoplay: true
        },
        key: configs.bitmovin.playerKey,
        analytics: {
            key: configs.bitmovin.analyticsKey,
            title: title,
            config: {
                origin: configs.appId
            }
        },
        style: {
            width: "100%",
            height: "100%"
        },
        logs: {
            // level: "debug"
        },
        tweaks: {
            file_protocol: true,
            app_id: configs.appId,
            minimal_backward_buffer_clearing_interval: 5
        }
    });

    player.setVideoElement(videoEl);

    return player;
};

export const destroyPlayer = () => {
    if (bitmovinPlayer) {
        bitmovinPlayer.destroy();
        bitmovinPlayer = null;
    }

    window.bitmovinPlayer = null;

    const wrapper = document.getElementById("bitmovin-wrapper");
    if (wrapper) {
        // Bitmovin moves videoEl into its container via setVideoElement.
        // Rescue it before removing the wrapper so Lightning's videoEl stays in the DOM.
        const videoEl = wrapper.querySelector("video");
        if (videoEl && wrapper.parentElement) {
            wrapper.parentElement.insertBefore(videoEl, wrapper);
        }
        wrapper.remove();
    }
};

const unload = (videoEl: HTMLVideoElement) => {
    bitmovinPlayer?.unload();

    console.log("videoEl", videoEl);
    if (videoEl) {
        videoEl.removeAttribute("src");
        Array.from(videoEl.querySelectorAll("track")).forEach((track) => track.remove());
    }
};

export const loader = (src: string, videoEl: HTMLVideoElement, config: VideoConfig = {}): Promise<void> => {
    return new Promise(async (resolve) => {
        unload(videoEl);

        videoEl.setAttribute("poster", "/static/images/poster.png");
        videoEl.setAttribute("crossorigin", "crossorigin");
        videoEl.style.setProperty("background", theme.colors.bg.primary);

        console.log("Loading bitmovin source:", src);
        bitmovinPlayer = window.bitmovinPlayer || initializeBitmovinPlayer(videoEl, src);

        window.bitmovinPlayer = bitmovinPlayer;

        const sourceConfig = determineBitmovinSourceType(src, config.drm, config.laUrl);
        console.log("Loading source:", sourceConfig);
        console.log("Loading Subtitles", config.subtitles);

        await bitmovinPlayer!.load({
            ...sourceConfig,
            subtitleTracks: config.subtitles
        });

        console.log("Bitmovin player loaded successfully");
        if (bitmovinPlayer?.getConfig().style) {
            bitmovinPlayer.getConfig().style!.nativeSubtitles = true;
        }

        toggleSubtitle(config.preferredSubLang || "off");

        bitmovinPlayer?.on(PlayerEvent.CueEnter, (e: PlayerEventBase) => {
            const cueEvent = e as CueEvent;
            if (config.onCueEnter) {
                config.onCueEnter(cueEvent.text);
            }
        });

        if (config.startPosition) {
            bitmovinPlayer?.seek(config.startPosition);
        }

        resolve();
    });
};

export const unloader = (videoEl: HTMLVideoElement): Promise<void> => {
    return new Promise((resolve) => {
        unload(videoEl);
        resolve();
    });
};

export const setPlaybackSpeed = (speed: number) => {
    bitmovinPlayer?.setPlaybackSpeed(speed);
    console.log(`Bitmovin playback speed set to ${speed}`);
};

export const toggleSubtitle = (lang: string) => {
    const subtitles = bitmovinPlayer?.subtitles.list();
    if (subtitles && subtitles.length > 0) {
        if (lang === "off") {
            const enabled = subtitles.find((sub) => sub.enabled === true);

            if (enabled) {
                bitmovinPlayer?.subtitles.disable(enabled.id);
            }
        } else {
            const sub = subtitles.find((sub) => sub.lang === lang);
            if (sub) {
                bitmovinPlayer?.subtitles.enable(sub.id);
            }
        }
    }
};

export const toggleAudio = (lang: string) => {
    if (!bitmovinPlayer) return;
    try {
        const audioTracks = bitmovinPlayer.getAvailableAudio();
        const track = audioTracks.find((t: any) => (t.lang || t.language) === lang);
        if (track) {
            bitmovinPlayer.setAudio(track.id);
            console.log(`Bitmovin audio track set to: ${lang}`);
        } else {
            console.warn(`Bitmovin audio track not found for language: ${lang}`);
        }
    } catch (e) {
        console.warn("Error setting Bitmovin audio track:", e);
    }
};

export const getPlaybackSpeed = () => {
    return bitmovinPlayer?.getPlaybackSpeed() || 1;
};

export const handleRewind = (rewindTime: number, callback: (isBitmovin: boolean) => void) => {
    if (bitmovinPlayer) {
        if (bitmovinPlayer.isPlaying()) {
            bitmovinPlayer.pause();
        }
        bitmovinPlayer.seek(rewindTime);
        callback(true);
    } else {
        callback(false);
    }
};

export const handleForward = (forwardTime: number, callback: (isBitmovin: boolean) => void) => {
    if (bitmovinPlayer) {
        if (bitmovinPlayer.isPlaying()) {
            bitmovinPlayer.pause();
        }
        bitmovinPlayer.seek(forwardTime);
        callback(true);
    } else {
        callback(false);
    }
};
